import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import CloseIcon from '@mui/icons-material/Close';
import {Grid, IconButton, SxProps, Typography, useMediaQuery, useTheme} from '@mui/material';

import {ComparisonCard} from '../../../../../../components/ComparisonCard/ComparisonCard';
import {LanguageContext} from '../../../../../../contexts/LanguageContext/LanguageContext';
import {greenColors, orangeColors} from '../../../../../../layouts/theme/designSystem';
import {CultureHouseElement} from '../../types/CultureHouseTypes';

interface CulturePillarModalProps {
  cultureHousePillar: CultureHouseElement | null;
  handleClose: () => void;
}

export default function CulturePillarModal({cultureHousePillar, handleClose}: CulturePillarModalProps) {
  const {t} = useTranslation();
  const currentLanguage = useContext(LanguageContext);
  const theme = useTheme();

  const legendStyles: SxProps = {
    color: greenColors[700],
    fontSize: {xs: '17px', sm: '20px'},
    ml: {xs: 1, sm: 2},
  };

  const legendIconStyles: SxProps = {
    width: '8px',
    height: '30px',
    borderRadius: 2,
  };

  const isXs = useMediaQuery(theme.breakpoints.only('xs'));

  const getVariant = () => {
    if (isXs) return 'h3';
    return 'h2';
  };

  const renderQuestionCards = () => {
    return cultureHousePillar?.surveyResults.questions.map((question, index) => (
      <ComparisonCard
        key={index}
        methodItem={question.methodItem}
        firstIndicatorValue={question.results.valueEmployees}
        secondIndicatorValue={question.results.valueManagement || 0}
        overlap={question.results.overlap}
        bubbleSize={40}
      />
    ));
  };

  return (
    <Grid container>
      <Grid item container justifyContent={'space-between'} alignItems={'center'} mb={1}>
        <Grid item xs={11}>
          <Typography variant='h4'>{cultureHousePillar?.pillarTitle[currentLanguage]}</Typography>
        </Grid>
        <Grid item xs={1} display={'flex'} justifyContent={'flex-end'}>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>

      <Typography variant={getVariant()} mb={1}>
        {t('companyCultureDashboard.cultureHouse.pillarCard.totalOverlap', {
          totalOverlap: cultureHousePillar?.surveyResults.totalOverlap,
        })}
      </Typography>

      <Grid item container mb={1}>
        <Grid item sx={legendIconStyles} bgcolor={greenColors[100]} />
        <Typography variant='body1' sx={legendStyles}>
          {t('companyCultureDashboard.cultureHouse.pillarCard.employees')}
        </Typography>
      </Grid>
      <Grid item container>
        <Grid item sx={legendIconStyles} bgcolor={orangeColors[100]} />
        <Typography variant='body1' sx={legendStyles}>
          {t('companyCultureDashboard.cultureHouse.pillarCard.management')}
        </Typography>
      </Grid>

      <Grid item mt={6} xs={12}>
        {renderQuestionCards()}
      </Grid>
    </Grid>
  );
}
